import { AlertCircle, CheckCircle2, Loader2, MinusCircle, ShieldCheck, UserPlus } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api as client } from '@/api/client';
import EarningsDisclaimer from '@/components/EarningsDisclaimer';
import GoogleTranslate from '@/components/GoogleTranslate';
import { useAuth } from '@/contexts/AuthContext';

const SignupPage = () => {
  const uid = useId();
  const { signup, authUser, bootLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Referral can arrive two ways:
  //   (a) Path segment from the catch-all route   →  /Ronk2326
  //   (b) Query string                            →  /signup?ref=Ronk2326
  const refFromPath = (params.ref || '').trim();
  const refFromQS = (new URLSearchParams(location.search).get('ref') || '').trim();
  const initialRef = refFromPath || refFromQS;

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [referralCode, setReferralCode] = useState(initialRef);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Referral resolution state — one of: "idle" | "checking" | "found" | "not_found"
  const [refState, setRefState] = useState('idle');
  const [resolvedRefUsername, setResolvedRefUsername] = useState('');
  const [resolvedRefInput, setResolvedRefInput] = useState('');

  useEffect(() => {
    if (!bootLoading && authUser) navigate('/dashboard', { replace: true });
  }, [bootLoading, authUser, navigate]);

  // Resolve the referral identifier against the backend whenever the input
  // changes (debounced lightly). Empty input → "idle" (will show "No direct
  // referral" pill).
  useEffect(() => {
    const value = (referralCode || '').trim();
    if (!value) {
      setRefState('idle');
      setResolvedRefUsername('');
      setResolvedRefInput('');
      return;
    }
    setRefState('checking');
    const handle = setTimeout(async () => {
      try {
        const r = await client.get('/api/public/referral-lookup', {
          params: { ref: value },
        });
        if (r.data?.username) {
          setRefState('found');
          setResolvedRefUsername(r.data.username);
          setResolvedRefInput(value);
        } else {
          setRefState('not_found');
          setResolvedRefUsername('');
          setResolvedRefInput(value);
        }
      } catch {
        setRefState('not_found');
        setResolvedRefUsername('');
        setResolvedRefInput(value);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [referralCode]);

  // Pill that summarizes referral state at the top of the form.
  // NOTE: must be declared BEFORE any early returns so React Hook order
  // stays stable across renders.
  const RefPill = useMemo(() => {
    if (refState === 'checking') {
      return (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(255,182,18,0.06)] border border-[rgba(255,182,18,0.22)] text-[12px]"
          data-testid="signup-ref-pill-checking"
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin text-[#FFC20E]" />
          <span className="text-[var(--sc-text-dim)]">Checking referral&hellip;</span>
        </div>
      );
    }
    if (refState === 'found') {
      return (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(255,182,18,0.10)] border border-[rgba(255,182,18,0.42)] text-[12px]"
          data-testid="signup-ref-pill-found"
        >
          <CheckCircle2 className="w-3.5 h-3.5 text-[#FFC20E]" />
          <span className="text-[var(--sc-text)]">
            Your direct referral:&nbsp;
            <span className="font-semibold text-[#FFC20E]" data-testid="signup-ref-pill-username">
              {resolvedRefUsername}
            </span>
          </span>
        </div>
      );
    }
    if (refState === 'not_found') {
      return (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(255,105,97,0.08)] border border-[rgba(255,105,97,0.32)] text-[12px]"
          data-testid="signup-ref-pill-not-found"
        >
          <AlertCircle className="w-3.5 h-3.5 text-[#FF8A82]" />
          <span className="text-[var(--sc-text)]">
            Referral <span className="font-semibold">&lsquo;{resolvedRefInput}&rsquo;</span> not found &mdash; you can still sign up without one.
          </span>
        </div>
      );
    }
    // idle: no referral entered
    return (
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.12)] text-[12px]"
        data-testid="signup-ref-pill-none"
      >
        <MinusCircle className="w-3.5 h-3.5 text-[var(--sc-text-mute)]" />
        <span className="text-[var(--sc-text-dim)]">No direct referral</span>
      </div>
    );
  }, [refState, resolvedRefUsername, resolvedRefInput]);

  if (bootLoading) {
    return (
      <div className="sc-canvas min-h-screen flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#FFB612]" />
      </div>
    );
  }
  if (authUser) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (username.trim().length < 3) {
      setErr('Username must be at least 3 characters.');
      return;
    }
    if (!/^[a-zA-Z0-9_.-]+$/.test(username.trim())) {
      setErr('Username may only contain letters, numbers, dot, dash, underscore.');
      return;
    }
    if (!email.trim()) {
      setErr('Email is required.');
      return;
    }
    if (password.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setErr('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await signup({
        username: username.trim(),
        email: email.trim(),
        password,
        referral_code: referralCode.trim() ? referralCode.trim() : undefined,
      });
      toast.success('Welcome aboard');
      navigate('/dashboard', { replace: true });
    } catch (e2) {
      const status = e2?.response?.status;
      const detail = e2?.response?.data?.detail;
      let msg;
      if (typeof detail === 'object' && detail !== null) msg = detail?.message || detail?.code;
      else if (Array.isArray(detail)) msg = detail[0]?.msg || 'Form validation failed';
      else if (typeof detail === 'string') msg = detail;
      else if (e2?.message?.includes('Network Error')) msg = "Network error — couldn't reach the server. Check your internet connection.";
      else msg = 'Sign-up failed';
      // Append HTTP status code so production failures are diagnosable
      // without DevTools. Format: "<message> (HTTP <code>)"
      if (status && !msg.includes('HTTP')) {
        msg = `${msg} (HTTP ${status})`;
      }
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="sc-canvas min-h-screen relative" data-testid="signup-page">
      <header className="absolute top-0 left-0 right-0 z-30 px-5 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="signup-brand-home">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#FFC20E] to-[#FFB612]">
            <ShieldCheck className="w-4 h-4 text-[#0a0a0b]" strokeWidth={2.6} />
          </div>
          <div className="font-display font-bold text-sm whitespace-nowrap">
            1Simple<span className="sc-gold-text">Cycler</span>
            <span className="text-[var(--sc-text-mute)] text-[11px] ml-1">3.0</span>
          </div>
        </Link>
        <GoogleTranslate />
      </header>

      <div className="flex items-center justify-center min-h-screen px-5 py-24">
        <form onSubmit={submit} className="sc-glass-strong rounded-2xl w-full max-w-md p-6 md:p-8 space-y-4 sc-fade-in" data-testid="signup-form">
          <div className="text-center">
            <div className="sc-pill mx-auto">
              <span className="sc-dot" />
              Create Account
            </div>
            <h1 className="font-display font-bold text-2xl md:text-3xl mt-3">Join 1SimpleCycler</h1>
            <p className="text-sm text-[var(--sc-text-dim)] mt-1">A referral connects you to a sponsor &mdash; optional.</p>
          </div>

          {RefPill}

          {err ? (
            <div
              className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-[rgba(255,105,97,0.08)] border border-[rgba(255,105,97,0.32)] text-[#FF8A82] text-[13px]"
              data-testid="signup-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3">
            <div>
              <label htmlFor={`${uid}-username`} className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1.5">
                Username
              </label>
              <input
                id={`${uid}-username`}
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="sc-input"
                data-testid="signup-username"
              />
            </div>
            <div>
              <label htmlFor={`${uid}-email`} className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1.5">
                Email
              </label>
              <input
                id={`${uid}-email`}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="sc-input"
                data-testid="signup-email"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor={`${uid}-password`} className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1.5">
                  Password
                </label>
                <input
                  id={`${uid}-password`}
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="sc-input"
                  data-testid="signup-password"
                />
              </div>
              <div>
                <label htmlFor={`${uid}-confirm`} className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1.5">
                  Confirm
                </label>
                <input
                  id={`${uid}-confirm`}
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="sc-input"
                  data-testid="signup-confirm"
                />
              </div>
            </div>
            <div>
              <label htmlFor={`${uid}-referral`} className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1.5">
                Referral
                <span className="text-[var(--sc-text-mute)] normal-case tracking-normal ml-1">(optional &mdash; sponsor&apos;s username)</span>
              </label>
              <input
                id={`${uid}-referral`}
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                className="sc-input"
                placeholder="Sponsor's username"
                data-testid="signup-referral"
              />
            </div>
          </div>

          <button type="submit" disabled={busy} className="sc-btn-white w-full" style={{ padding: '14px 22px', fontSize: 13 }} data-testid="signup-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Create Account
          </button>

          <div className="text-center text-[12px] text-[var(--sc-text-dim)]">
            Already a member?{' '}
            <Link to="/login" className="text-[#FFC20E] font-semibold hover:underline" data-testid="signup-to-login">
              Sign in
            </Link>
          </div>
        </form>
      </div>

      <EarningsDisclaimer variant="compact" />
    </main>
  );
};

export default SignupPage;
